<template>
  <div class="analyzer-output">
    <div class="ambiguity-total has-text-info">
      Ambiguity = [Lemma: {{ ambigFormat(sortedTranslatedOutput.ambiguity.lemmas) }} | Analysis: {{ ambigFormat(sortedTranslatedOutput.ambiguity.analyses) }}]
    </div>
    <div class="analysis-container collapsible" v-for="(word, index) in sortedTranslatedOutput.words" v-bind:key="index">
      <div class="word collapse-header">
        <a class="has-text-primary collapse-btn"></a>
        <span dir='rtl'>{{ word.word }}</span>
        <span class="ambiguity-word has-text-info">Ambiguity = [Lemma: {{ ambigFormat(word.ambiguity.lemmas) }} | Analysis: {{ ambigFormat(word.ambiguity.analyses) }}]</span>
      </div>
      <div class="collapse-body">
        <div class="lemma-group collapsible" v-if="Object.keys(word.lemmas).length === 0">
          <div class="lemma collapse-header"></div>
          <div class="collapse-body">
            No Analysis
          </div>
        </div>
        <div class="lemma-group collapsible" v-if="Object.keys(word.lemmas).length > 0" v-for="(lemma, index) in word.sortedLemmas" v-bind:key="index">
          <div class="lemma collapse-header">
            <a class="has-text-primary collapse-btn"></a>
            <span v-for="(sublemma, index) in lemma.split(' ')" v-bind:key="index">
              <span dir="rtl" v-if="index === 0">{{ sublemma }}</span>
              <span v-if="index > 0">{{ sublemma }}</span>
            </span>
            <span> &#8212; {{ word.lemmas[lemma].glossString }} </span>
          </div>
          <div class="collapse-body">
            <table class="analyses table" v-if="rawView">
              <tbody>
                <tr class="analysis" v-for="(analysis, index) in word.lemmas[lemma].raw" v-bind:key="'a-' + index">
                  <td class="feat-val" v-for="(feat, index) in featOrder" v-bind:key="index">
                    <span class="feat">{{ feat }}:</span>
                    <!-- Hack to get around Chrome display issues -->
                    <span class="val" dir="rtl"  v-if="tokFeats[feat] == 1">
                      <span v-for="(tokfeats, index) in [parseToks(analysis[feat])]" v-bind:key="'t-' + index">
                        <span dir="rtl" v-for="(prefix, index) in tokfeats.prefixes" v-bind:key="'p-' + index">
                          <span dir="rtl">{{ prefix }}</span><span dir="rtl">+_</span>
                        </span>
                        <span dir="rtl" v-for="(stem, index) in tokfeats.stems" v-bind:key="'s-' + index">
                          <span dir="rtl">{{ stem }}</span>
                        </span>
                        <span dir="rtl" v-for="(suffix, index) in tokfeats.suffixes" v-bind:key="'u-' + index">
                          <span dir="rtl">_+</span><span dir="rtl">{{ suffix }}</span>
                        </span>
                      </span>
                    </span>
                    <span class="val" v-else-if="feat == 'lex'">{{ analysis.lex.split(/_|-/)[0] }}</span>
                    <span class="val" v-bind:dir="ltrFeats[feat] ? 'ltr' : 'rtl'" v-else>{{ (feat == 'caphi' && analysis.source === 'backoff') ? '*' : analysis[feat] }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <table class="analyses table" v-if="!rawView">
              <tbody>
                <tr class="analysis" v-for="(analysis, index) in word.lemmas[lemma].translated" v-bind:key="'a' + index">
                  <td class="feat-val simple" dir="rtl"><b>{{ analysis.diac }}</b></td>
                  <td class="feat-val simple">
                    <i v-if="analysis.source === 'backoff'">N/A</i>
                    <i v-if="analysis.source !== 'backoff'">{{ analysis.caphi }}</i>
                  </td>
                  <td class="feat-val simple" v-for="(prc, index) in analysis.prc" v-if="prc && prc.lex && prc.pos" v-bind:key="'p' + index">
                    <span dir="rtl">{{ prc.lex }}</span>/<span>{{ prc.pos }}</span>
                  </td>
                  <td class="feat-val simple">
                    <span v-if="analysis.morphoFeat && analysis.morphoFeat.length">[{{ analysis.morphoFeat }}]</span>
                  </td>
                  <td class="feat-val simple" v-for="(enc, index) in analysis.enc" v-if="enc && enc.lex && enc.pos" v-bind:key=" 'e' + index">
                    <span dir="rtl">{{ enc.lex }}</span>/<span>{{ enc.pos }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash';
// eslint-disable-next-line
import numeral from 'numeral';
import { translations } from './translations.json';

const prcFeats = ['prc3', 'prc2', 'prc1', 'prc0'];
const tokFeats = {
  atbtok: 1,
  atbseg: 1,
  d1tok: 1,
  d1seg: 1,
  d2tok: 1,
  d2seg: 1,
  d3tok: 1,
  d3seg: 1,
};
const ltrFeats = {
  prc0: 1,
  prc1: 1,
  prc2: 1,
  prc3: 1,
  enc0: 1,
  enc1: 1,
  enc2: 1,
  enc3: 1,
  gloss: 1,
  caphi: 1,
  pos: 1,
  asp: 1,
  vox: 1,
  mod: 1,
  gen: 1,
  per: 1,
  num: 1,
  stt: 1,
  cas: 1,
  rat: 1,
  source: 1,
  stemcat: 1,
  bw: 1,
  pattern: 1,
};

// eslint-disable-next-line
const encFeats = ['enc0'];

function parseClitics(analysis) {
  const result = {
    enc: [],
    prc: [],
  };

  const d3tok = analysis.d3tok || '';
  const d3subtoks = d3tok.split('_');
  const prcLex = d3subtoks.filter(t => t[t.length - 1] === '+');
  const prcPOS = [];
  const encLex = d3subtoks.filter(t => t[0] === '+');
  const encPOS = [];

  for (let p = 0; p < prcFeats.length; p += 1) {
    const featVal = analysis[prcFeats[p]] || '';
    if (featVal !== '0') {
      const subtoks = featVal.split('_');
      prcPOS.push(subtoks[1]);
    }
  }

  result.prc = lodash.zipWith(prcLex, prcPOS, (lex, pos) => ({ lex, pos }));

  for (let e = 0; e < encFeats.length; e += 1) {
    const featVal = analysis[encFeats[e]] || '';
    if (featVal !== '0') {
      encPOS.push(featVal);
    }
  }

  result.enc = lodash.zipWith(encLex, encPOS, (lex, pos) => ({ lex, pos }));

  return result;
}

function scoreLemma(lemma) {
  const result = {
    sources: {
      lex: 0.0,
      spvar: 0.0,
      digit: 0.0,
      punc: 0.0,
      foreign: 0.0,
    },
    lexLen: 0,
  };

  if (lemma.raw.length === 0) {
    return result;
  }

  const lexStripped = lemma.raw[0].lex.split(/_|-/)[0];
  result.lexLen = lexStripped.length;

  for (let i = 0; i < lemma.raw.length; i += 1) {
    const lemmaSource = lemma.raw[i].source || null;
    if (lemmaSource === 'lex' || lemmaSource === 'spvar' || lemmaSource === 'digit' ||
      lemmaSource === 'punc' || lemmaSource === 'foreign') {
      result.sources[lemmaSource] += 1;
    }
  }

  const sourceTypes = Object.keys(result.sources);
  for (let i = 0; i < sourceTypes.length; i += 1) {
    result.sources[sourceTypes[i]] /= lemma.raw.length;
  }

  return result;
}

export default {
  name: 'AnalyzerOutput',
  components: {},
  props: {
    outputData: {
      type: Object,
      default: () => ({ words: [] }),
    },
    lang: {
      type: String,
      default: () => ('en'),
    },
    rawView: {
      type: Boolean,
      default: () => (false),
    },
  },
  data() {
    return {
      numeral,
      tokFeats,
      ltrFeats,
      featOrder: [
        'diac',
        'caphi',
        'lex',
        'bw',
        'gloss',
        'pos',
        'prc3',
        'prc2',
        'prc1',
        'prc0',
        'enc0',
        'asp',
        'vox',
        'mod',
        'gen',
        'num',
        'stt',
        'cas',
        'per',
        'rat',
        'source',
        'stem',
        'stemcat',
        'd3tok',
        'd3seg',
        'd2tok',
        'd2seg',
        'd1tok',
        'd1seg',
        'atbtok',
        'atbseg',
      ],
    };
  },

  computed: {
    sortedTranslatedOutput() {
      const result = {
        words: [],
        ambiguity: {
          lemmas: 1,
          analyses: 1,
        },
      };

      for (let w = 0; w < this.outputData.words.length; w += 1) {
        const word = this.outputData.words[w].word;
        const analyses = this.outputData.words[w].analyses || [];
        const lemmas = {};

        analyses.sort((a, b) => {
          if (a.source === 'lex' && b.source !== 'lex') return -1;
          if (a.source !== 'lex' && b.source === 'lex') return 1;

          const aBw = a.bw || '';
          const bBw = b.bw || '';

          if (aBw > bBw) return 1;
          if (aBw < bBw) return -1;

          return 0;
        });

        for (let a = 0; a < analyses.length; a += 1) {
          const analysis = analyses[a];
          const lemma = analysis.lex.split(/_|-/)[0];
          const pos = analysis.pos;
          const root = analysis.root === 'ٌطWص' ? '-' : (analysis.root || 'N/A');
          let stemgloss = analysis.stemgloss ? analysis.stemgloss : analysis.gloss;
          stemgloss = (stemgloss === 'NO_ANALYSIS' ? 'N/A' : stemgloss);
          // const lemmaPOS = `${lemma} \u2014 ${pos} \u2014 [${root}] \u2014 ${stemgloss}`;
          const lemmaPOS = `${lemma} \u2014 ${pos} \u2014 [${root}]`;
          const translated = {
            diac: analysis.diac,
            caphi: analysis.caphi,
            root: analysis.root,
            stemgloss,
            morphoFeat: '',
            prc: [],
            enc: [],
            source: analysis.source,
          };

          // TODO: Remove this. This is just a quick fix until db is fixed.
          if (analysis.rat === 'b') {
            analysis.rat = 'i';
          } else if (analysis.rat === 'y') {
            analysis.rat = 'na';
          }

          const order = this.lang === 'ar' ?
            translations.pos[pos].featsAR : translations.pos[pos].featsEN;
          if (translations.pos[pos]) {
            const morphoFeats = [];
            for (let f = 0; f < order.length; f += 1) {
              const feat = order[f];
              const featVal = (analysis[feat] === 'u' || analysis[feat] === 'na' || analysis[feat] === '-' || analysis[feat] === null) ?
                '' : (translations.feats[feat].values[analysis[feat]][this.lang] || '');

              morphoFeats.push(featVal);
            }
            translated.morphoFeat = morphoFeats.join(' ').trim();
          }

          const clitics = parseClitics(analysis);

          translated.prc = clitics.prc;
          translated.enc = clitics.enc;

          if (Object.hasOwnProperty.call(lemmas, lemmaPOS)) {
            const glosses = analysis.stemgloss.split(/;/);
            for (let g = 0; g < glosses.length; g += 1) {
              lemmas[lemmaPOS].glosses[glosses[g]] = 1;
            }
            lemmas[lemmaPOS].raw.push(analysis);
            lemmas[lemmaPOS].translated.push(translated);
          } else {
            lemmas[lemmaPOS] = {};
            lemmas[lemmaPOS].glosses = {};
            const glosses = analysis.stemgloss.split(/;/);
            for (let i = 0; i < glosses.length; i += 1) {
              lemmas[lemmaPOS].glosses[glosses[i]] = 1;
            }
            lemmas[lemmaPOS].raw = [analysis];
            lemmas[lemmaPOS].translated = [translated];
          }
        }

        // Concatenate unique glosses as a single string
        const lemmaPOSs = Object.keys(lemmas);
        for (let lemmaNdx = 0; lemmaNdx < lemmaPOSs.length; lemmaNdx += 1) {
          const lemmaPOS = lemmaPOSs[lemmaNdx];
          const uniqueGlosses = lodash.uniq(Object.keys(lemmas[lemmaPOS].glosses));
          uniqueGlosses.sort();
          const glossString = uniqueGlosses.join(';');
          lemmas[lemmaPOS].glossString = glossString;
        }

        const ambiguity = {
          lemmas: Math.max(Object.keys(lemmas).length, 1),
          analyses: Math.max(analyses.length, 1),
        };

        // Sort lemmas by relevance score
        const lemmaScores = {};
        const lemmaKeys = Object.keys(lemmas);

        for (let i = 0; i < lemmaKeys.length; i += 1) {
          const lemmaKey = lemmaKeys[i];
          const lemmaScore = scoreLemma(lemmas[lemmaKey]);
          lemmaScores[lemmaKey] = lemmaScore;
        }

        lemmaKeys.sort((a, b) => {
          if (lemmaScores[a].sources.lex > lemmaScores[b].sources.lex) return -1;
          if (lemmaScores[a].sources.lex < lemmaScores[b].sources.lex) return 1;
          if (lemmaScores[a].sources.spvar > lemmaScores[b].sources.spvar) return -1;
          if (lemmaScores[a].sources.spvar < lemmaScores[b].sources.spvar) return 1;
          if (lemmaScores[a].sources.digit > lemmaScores[b].sources.digit) return -1;
          if (lemmaScores[a].sources.digit < lemmaScores[b].sources.digit) return 1;
          if (lemmaScores[a].sources.punc > lemmaScores[b].sources.punc) return -1;
          if (lemmaScores[a].sources.punc < lemmaScores[b].sources.punc) return 1;
          if (lemmaScores[a].sources.foreign > lemmaScores[b].sources.foreign) return -1;
          if (lemmaScores[a].sources.foreign < lemmaScores[b].sources.foreign) return 1;
          if (lemmaScores[a].lexLen > lemmaScores[b].lexLen) return -1;
          if (lemmaScores[a].lexLen < lemmaScores[b].lexLen) return 1;
          return 0;
        });

        result.words.push({
          word,
          lemmas,
          sortedLemmas: lemmaKeys,
          ambiguity,
        });

        result.ambiguity.lemmas *= ambiguity.lemmas;
        result.ambiguity.analyses *= ambiguity.analyses;
      }

      return result;
    },
  },

  methods: {
    ambigFormat(n) {
      if (n === Infinity || n === undefined || isNaN(n)) {
        return n;
      }

      return n < 10000 ? numeral(n).format('0,0') : numeral(n).format('0,0.000e+0');
    },
    parseToks(s) {
      const result = {
        prefixes: [],
        stems: [],
        suffixes: [],
      };

      const toks = s.split('_');

      for (let t = 0; t < toks.length; t += 1) {
        const token = toks[t];

        if (token.startsWith('+')) {
          result.suffixes.push(token.substring(1));
        } else if (token.endsWith('+')) {
          result.prefixes.push(token.substring(0, token.length - 1));
        } else {
          result.stems.push(token);
        }
      }

      return result;
    },
  },

  watch: {},

  mounted() {
    // eslint-disable-next-line
    $('.collapse-btn').click(function () {
      const body = $(this).parent('.collapse-header').parent('.collapsible')
        .children('.collapse-body');
      if (body.hasClass('collapsed')) {
        body.removeClass('collapsed');
        $(this).removeClass('collapsed');
      } else {
        body.addClass('collapsed');
        $(this).addClass('collapsed');
      }
      return 0;
    });
  },
};
</script>

<style lang="scss" scoped>

td.simple {
  display: inline-block;
}

.analyzer-output {
  width: auto;
}

.analysis-container {
  display: block;
  margin-bottom: 1.5em;
}

.ambiguity-word {
  position: relative;
  top: -0.15em;
  margin-left: 1em;
  font-size: 0.8em;
  font-weight: normal;
}

.ambiguity-total {
  font-size: 1.3em;
  margin-bottom: 0.5em;
}

.word {
  font-size: 1.5em;
  font-weight: bold;
}

.lemma {
  font-size: 1.5em;
}

.lemma-group {
  margin-left: 2em;
}

.analyses {
  margin-left: 2em;
  margin-bottom: 1.5em;
  width: auto;
  white-space: nowrap;
}

.analysis:hover {
  background-color: #f1adad;
}

.feat-val {
  font-size: 1.3em;
  padding: 0.1em 0.5em;
  white-space: nowrap;
  border: none !important;
}

.feat {
  font-weight: bold;
}

.collapse-btn {
  display: inline-block;
  width: 1em;
  height: 1em;
  line-height: 0.75em;
  border-style: solid;
  border-width: 1px;
  border-radius: 0.5em;
  text-align: center;
}

.collapse-btn::after {
  content: '-';
}

.collapse-btn.collapsed::after {
  content: '+';
}

.collapse-body.collapsed {
  display: None;
}

.output-control {
  margin-bottom: 1.5em;
}
</style>



// WEBPACK FOOTER //
// src/components/Shared/AnalyzerOutput.vue